import { motion, useReducedMotion } from "framer-motion";
import { Flag, Send, TriangleAlert, X } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useDialogFocus } from "../lib/useDialogFocus";

export type ConfirmRequest = {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
};

function ModalShell({ children, labelledBy, onClose }: { children: React.ReactNode; labelledBy: string; onClose: () => void }) {
  const reduceMotion = useReducedMotion();
  const dialogRef = useDialogFocus<HTMLElement>(true, onClose);
  return createPortal(<motion.div
    className="modal-backdrop"
    role="presentation"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: reduceMotion ? 0 : .22 }}
    onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
  >
    <motion.section
      ref={dialogRef}
      className="confirm-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      tabIndex={-1}
      initial={reduceMotion ? false : { opacity: 0, y: 22, scale: .96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 14, scale: .98 }}
      transition={{ duration: reduceMotion ? 0 : .3, ease: [0.22, 1, 0.36, 1] }}
    >
      <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть окно"><X size={18} /></button>
      {children}
    </motion.section>
  </motion.div>, document.body);
}

export function ConfirmDialog({ request, onClose }: { request: ConfirmRequest; onClose: () => void }) {
  return <ModalShell labelledBy="confirm-modal-title" onClose={onClose}>
    <span className="confirm-seal danger"><TriangleAlert /></span>
    <h2 id="confirm-modal-title">{request.title}</h2>
    <p>{request.message}</p>
    <div className="confirm-actions">
      <button type="button" className="ghost-button" onClick={onClose} data-dialog-initial-focus>{request.cancelLabel || "Оставить как есть"}</button>
      <button type="button" className="primary-button confirm-danger" onClick={() => { onClose(); void request.onConfirm(); }}><span>{request.confirmLabel}</span></button>
    </div>
  </ModalShell>;
}

const REASON_LIMIT = 500;

export function ReasonDialog({ title, message, onClose, onSubmit }: {
  title: string; message: string; onClose: () => void; onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const tooShort = reason.trim().length < 3;
  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (tooShort) return;
    onClose();
    onSubmit(reason.trim().slice(0, REASON_LIMIT));
  }
  return <ModalShell labelledBy="reason-modal-title" onClose={onClose}>
    <span className="confirm-seal"><Flag /></span>
    <h2 id="reason-modal-title">{title}</h2>
    <p>{message}</p>
    <form className="reason-form" onSubmit={submit}>
      <label>
        <span className="visually-hidden">Причина жалобы</span>
        <textarea
          data-dialog-initial-focus
          value={reason}
          maxLength={REASON_LIMIT}
          rows={4}
          placeholder="Например: факты перевраны, оскорбления или спам…"
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      <div className="reason-meta"><small>{tooShort ? "Опишите причину хотя бы парой слов" : "Жалоба готова к отправке"}</small><small>{reason.length}/{REASON_LIMIT}</small></div>
      <div className="confirm-actions">
        <button type="button" className="ghost-button" onClick={onClose}>Передумать</button>
        <button type="submit" className="primary-button confirm-danger" disabled={tooShort}><span>Отправить жалобу</span><Send size={16} /></button>
      </div>
    </form>
  </ModalShell>;
}
